import React, { useEffect, useState } from 'react';
import {
  FaUser, FaClock, FaFileAlt, FaMobileAlt,
  FaUserFriends, FaLifeRing, FaUserShield, FaHistory,
  FaGift
} from 'react-icons/fa';
import {
  Container, Col,
  Row
} from 'reactstrap';
import { Tabs, Tab, Card } from 'react-bootstrap';
import {
  useLocation, useHistory, useRouteMatch,
  Switch, Route, Redirect
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setResolvedUserId, setUserData as setUserDataInStore } from '../../../../store/slices/userManagementSlice.js';
import ProfileEditTab from './profile.jsx';

import TransactionsList from './transactions.jsx';
import DeviceList from './devices.jsx';
import FollowingUsers from './following.jsx';
import FollowedUsers from './followers.jsx';
import UserSupport from '../edit/support.jsx';
import UserModeration from './moderation.jsx';
import ActivityList from './activities.jsx';
import Loader from '../../../common/Loader.js';
import '../styles.css';
import SubscriptionLists from './subscriptions.jsx';
import GiftsHistory from './gifts.jsx';
import AddSubscriptionForm from './add_sub.jsx';
import Breadcrumb from '../../../common/Breadcrumb/Breadcrumb';


// ─── Main EditUser Layout ─────────────────────────────────────────────────────
const EditUserLayout = ({ groupMemberView = false }) => {
  const location = useLocation();
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const basePath = groupMemberView ? '/group/member-details' : path;
  const baseUrl = groupMemberView ? '/group/member-details' : url;
  const dispatch = useDispatch();
  const storedUserData = useSelector(state => state.userManagement.userData) || {};
  let storedGroupMemberData = {};
  if (groupMemberView) {
    try {
      storedGroupMemberData = JSON.parse(localStorage.getItem('group_member_details_user') || '{}');
    } catch (_) {
      storedGroupMemberData = {};
    }
  }
  const userData = groupMemberView
    ? (location.state?.values || (storedGroupMemberData?.user_id ? storedGroupMemberData : storedUserData))
    : (storedUserData?.user_id ? storedUserData : (location.state?.values || {}));
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const isAddSubscriptionPage = location.pathname.includes('/add_sub');

  useEffect(() => {
    if (groupMemberView && userData?.user_id) {
      dispatch(setUserDataInStore(userData));
      dispatch(setResolvedUserId(userData.user_id));
    }
  }, [dispatch, groupMemberView, userData?.user_id]);

  const getPublisherConfigInfo = (keyName) => {
    try {
      const configArr = JSON.parse(localStorage.getItem('publisherConfig') || '[]');
      const found = configArr.find(i => i.key_name === keyName);
      return found ? found.key_value : false;
    } catch (_) { return false; }
  };

  const handleProfileSaveSuccess = (updatedData) => {
    dispatch(setUserDataInStore({
      ...userData,
      first_name: updatedData.first_name,
      last_name: updatedData.last_name,
      user_email: updatedData.email,
    }));
  };

  const tabs = [
    { segment: 'profile', label: 'Overview', icon: FaUser },
    { segment: 'subscriptions', label: 'Subscription Details', icon: FaFileAlt },
    { segment: 'transactions', label: 'Transaction History', icon: FaClock },
    { segment: 'device', label: 'Connected Device', icon: FaMobileAlt },
    ...(getPublisherConfigInfo('community_needed') === 'True'
      ? [
        { segment: 'followers', label: 'Followers', icon: FaUserFriends },
        { segment: 'following', label: 'Following', icon: FaUserFriends },
      ]
      : []),
    ...(getPublisherConfigInfo('gift_needed') === 'True' ? [
      { segment: 'gifts', label: 'Gift History', icon: FaGift }
    ] : []),
    { segment: 'moderation', label: 'Moderation', icon: FaUserShield },
    { segment: 'support', label: 'Support', icon: FaLifeRing },
    { segment: 'activity', label: 'Activity', icon: FaHistory },
  ];

  const visibleTabs = groupMemberView
    ? tabs.filter(({ segment }) => !['subscriptions', 'transactions', 'gifts'].includes(segment))
    : tabs;
  const contentMinHeight = { minHeight: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const activeTab = tabs.find(({ segment }) => location.pathname.includes(`/${segment}`))?.segment || 'profile';
  const renderInCard = (content) => (
    <Card className="user-tab-content-card m-0 mt-2">
      <Card.Body style={{ margin: 12 }}>{content}</Card.Body>
    </Card>
  );

  if (isAddSubscriptionPage) {
    if (!userData?.user_id) {
      return (
        <div style={contentMinHeight}>
          <Loader />
        </div>
      );
    }

    return <AddSubscriptionForm />;
  }

  return (
    <>
      <div className="bread-crumbs-container col-lg-12 border-0 fit-max-width">
        <div className="bread-crumbs">
          <div className="heading pl-1 pt-2 ml-0">
            <Breadcrumb items={groupMemberView ? [{ label: "My Group" }, { label: "Members", to: "/group/members" }] : [{ label: "User Management" }, { label: "User List", to: "/operations/UserManagment" }]} />
            <h1>{groupMemberView ? "Member Details" : "User Details"} -{userData?.user_email || 'Loading...'}</h1>
          </div>
        </div>
      </div>

      <Container className="body-inner-wrapper px-3 pb-3 border-0" id="p-t-0">
        <div className="d-md-flex flex-column">
          <Card className="user-tab-content-card user-details-tabs m-0 mt-2">
            <Card.Body style={{ margin: '12px 12px 0px 12px' }}>
              {/* Tab Navigation — each tab is a real route link */}
              <div className="user-details-tabs-scroll">
                <Tabs
                  id="user-details-tabs"
                  variant='pills'
                  activeKey={activeTab}
                  onSelect={(selectedKey) => {
                    if (!selectedKey) return;
                    history.push(`${baseUrl}/${selectedKey}`);
                    if (selectedKey === 'activity') setActivityRefreshKey(k => k + 1);
                  }}
                  className="user-details-tabs-nav mb-0"
                >
                  {visibleTabs.map(({ segment, label, icon: Icon }) => (
                    <Tab
                      key={segment}
                      eventKey={segment}
                      tabClassName="user-details-tab-item"
                      title={(
                        <div className='d-flex align-items-center'>
                          <Icon size={16} style={{ marginRight: '8px' }} />
                          {label}
                        </div>
                      )}
                    />
                  ))}
                </Tabs>
              </div>

            </Card.Body>
          </Card>


          {/* Tab Content — each tab renders at its own URL */}
          <Row className='mt-2'>
            <Col xs={12} md={12} lg={12}>
              {!userData?.user_id ? (
                <div style={contentMinHeight}>
                  <Loader />
                </div>
              ) : (
                <Switch location={location}>
                  {/* Default: redirect to profile tab */}
                  <Redirect exact from={basePath} to={`${baseUrl}/profile`} />

                  <Route exact path={`${basePath}/profile`}>
                    {renderInCard(<ProfileEditTab onSaveSuccess={handleProfileSaveSuccess} groupMemberView={groupMemberView} />)}
                  </Route>

                  <Route exact path={`${basePath}/subscriptions`}>
                    {renderInCard(<SubscriptionLists />)}
                  </Route>

                  {getPublisherConfigInfo('gift_needed') === 'True' && <Route exact path={`${basePath}/gifts`}>
                    {renderInCard(<GiftsHistory />)}
                  </Route>}

                  <Route exact path={`${basePath}/transactions`}>
                    {renderInCard(<TransactionsList userData={userData} />)}
                  </Route>

                  <Route exact path={`${basePath}/device`}>
                    {renderInCard(<DeviceList />)}
                  </Route>

                  <Route exact path={`${basePath}/moderation`}>
                    {renderInCard(<UserModeration />)}
                  </Route>

                  <Route exact path={`${basePath}/support`}>
                    {renderInCard(<UserSupport />)}
                  </Route>

                  <Route exact path={`${basePath}/activity`}>
                    {renderInCard(
                      <ActivityList
                        refreshKey={activityRefreshKey}
                      />
                    )}
                  </Route>

                  {/* Keep the conditional routes at last, since other routes followed by this uncontrolled 
                  routes will not consider
                  */}
                  {getPublisherConfigInfo('community_needed') === 'True' && (
                    <>
                      <Route exact path={`${basePath}/followers`}>
                        {renderInCard(<FollowedUsers />)}
                      </Route>
                      <Route exact path={`${basePath}/following`}>
                        {renderInCard(<FollowingUsers />)}
                      </Route>
                    </>
                  )}

                </Switch>
              )}
            </Col>
          </Row>

        </div>
      </Container>
    </>
  );
};

export default EditUserLayout;
